import React from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';

import { StatisticsUnit } from '../../../../core/src/shared/statistics-config';

import { FormatNumberOpts } from '../../utils/format-number';

import { colors } from '../../theme/palette';
import { fontFamily } from '../../theme/typography';

import { RouterOutput } from '../../api/trpc';

import { Mode, Variant } from './chart-box.use-mode-select';
import { getNumberFormat, getSubStatColor } from './chart-box.utils';
import { CustomTooltip } from './chart-box.custom-tooltip';
import { ChartDataVariableId } from '../../utils/statistics';

type Options = {
  data: RouterOutput['getStats'];
  mode: Mode;
  unit: StatisticsUnit;
  yAxisDomain?: AxisDomain;
  yAxisTickFormatter: (x: any) => string;
  mobile: boolean;
  formatNumber: (value: number, opts: FormatNumberOpts) => string;
  maxValue?: number;
  variant: Variant.Bar | Variant.BarStacked;
  variableId: ChartDataVariableId;
  getSubStatsLabel?: (key: string) => string;
};

export const getBarChart = ({
  data,
  mode,
  unit,
  yAxisDomain,
  yAxisTickFormatter,
  mobile,
  formatNumber,
  maxValue,
  variant,
  variableId,
  getSubStatsLabel,
}: Options) => {
  const showBarLabelList = mode === Mode.Week;

  const tickWithFractionDigits =
    mode === Mode.Day && unit === StatisticsUnit.Kilowatt;
  const tickUseMegawatt =
    mode === Mode.Year && unit === StatisticsUnit.Kilowatt;

  const stacked = variant === Variant.BarStacked && data.subStatsKeys.length;

  return (
    <BarChart data={data.sortedData}>
      <CartesianGrid
        strokeWidth={0.25}
        stroke={colors.darkBlue}
        strokeOpacity={0.4}
        vertical={false}
      />
      <Tooltip
        content={
          // eslint-disable-next-line
          // @ts-ignore
          <CustomTooltip
            mode={mode}
            unit={unit}
            variant={variant}
            variableId={variableId}
            getSubStatsLabel={getSubStatsLabel}
          />
        }
        cursor={{ fill: colors.darkBlue, opacity: 0.15 }}
      />

      {!stacked && (
        <Bar dataKey="value" fill={colors.primary}>
          {showBarLabelList && (
            <LabelList
              dataKey="value"
              position="insideBottom"
              fontFamily={fontFamily}
              fill={colors.white}
              offset={12}
              formatter={(value) => {
                if (typeof value !== 'number') {
                  return '';
                }

                const [adjustedValue, opts] = getNumberFormat(
                  value,
                  unit,
                  mode,
                  Variant.Bar
                );

                if (mobile) {
                  opts.minimumFractionDigits = 0;
                  opts.maximumFractionDigits = 0;
                }

                return formatNumber(adjustedValue, opts);
              }}
            />
          )}
        </Bar>
      )}

      {stacked &&
        data.subStatsKeys.map((key, index) => {
          const color = getSubStatColor(index, data.subStatsKeys.length);

          return (
            <Bar
              dataKey={`subStats.${key}`}
              name="kW"
              stackId="1"
              fill={`${color}`}
              key={key}
            />
          );
        })}

      <XAxis
        dataKey="at"
        style={{
          fontFamily,
          color: colors.darkBlue,
          top: 20,
          transform: 'translateY(8px)',
        }}
        axisLine={false}
        tickLine={false}
        interval={getTicksInterval()}
        tickFormatter={yAxisTickFormatter}
      />
      <YAxis
        width={getYAxisWidth(
          mobile,
          maxValue,
          tickWithFractionDigits || tickUseMegawatt,
          tickUseMegawatt
        )}
        style={{
          fontFamily,
          color: colors.darkBlue,
          transform: 'translateX(-6px)',
        }}
        axisLine={false}
        tickLine={false}
        tickFormatter={(value) => {
          const fractionDigits = tickUseMegawatt
            ? 2
            : tickWithFractionDigits
            ? 1
            : 0;

          const adjustedValue = tickUseMegawatt ? value / 1000 : value;

          return formatNumber(adjustedValue, {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits,
          });
        }}
        domain={yAxisDomain}
      />
    </BarChart>
  );
};

const getYAxisWidth = (
  mobile: boolean,
  maxValue: number | undefined,
  tickWithFractionDigits: boolean,
  tickUseMegawatt: boolean
) => {
  if (!maxValue) return 30;

  const digits = countDigits(maxValue * 1.1);
  const spaces = Math.floor(digits / 4);

  const baseWidth = 10;
  const digitWidth = mobile ? 7 : 9;
  const fractionDigitsWidth = mobile ? 8 : 10;

  const width =
    baseWidth +
    (digits + spaces + (tickUseMegawatt ? -1 : 0)) * digitWidth +
    (tickWithFractionDigits ? fractionDigitsWidth : 0);
  return width;
};

const getTicksInterval = () => {
  return undefined;
};

function countDigits(number: number) {
  if (number === 0) return 1;
  return Math.floor(Math.log10(Math.abs(number))) + 1;
}
